import createTheme from '@mui/material/styles/createTheme';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import SelectInput, { SelectChangeEvent } from '@mui/material/Select';
import { SELECT_EMPTY } from '../../../core/consts';
import { IsNullOrEmpty } from '../../../core/utility';
import { CommonInputProps } from '../Models';

const theme = createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    transform: 'translate(14px, 11px) scale(1)',
                    '&.Mui-focused': {
                        transform: 'translate(14px, -9px) scale(0.85)',
                    },
                    '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -9px) scale(0.85)',
                    }
                },
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    height: '23px',
                    paddingTop: '10px',
                    fontSize: '15px',
                }
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '15px'
                }
            }
        }
    },
});

interface CustomSelectOptions<T> {
    data: T[];
    displayField: keyof T;
    displayValue: keyof T;
    selected: any
}

interface CustomSelectProps<T> extends CommonInputProps {
    options: CustomSelectOptions<T>;
    loading?: boolean;
    startAdornment?: React.ReactNode;
    onChange: (event: SelectChangeEvent) => void;
}

const Select = <T extends {}>(props: CustomSelectProps<T>) => {

    const items = props.options.data;

    return (
        <ThemeProvider theme={theme}>
            <FormControl fullWidth size='small' className='custom-select'>
                <InputLabel id={props.id ? props.id + '-label' : undefined} error={!IsNullOrEmpty(props.errorText)}>
                    {`${props.caption} ${props.required ? '*' : ''}`}
                </InputLabel>
                <SelectInput
                    id={props.id}
                    name={props.name}
                    className={props.className}
                    value={props.options.selected === null ? SELECT_EMPTY : props.options.selected}
                    size="small"
                    onChange={props.onChange}
                    disabled={props.disabled}
                    readOnly={props.readOnly}
                    input={(
                        <OutlinedInput
                            label={props.caption}
                            required={props.required} />
                    )}
                    error={!IsNullOrEmpty(props.errorText)}
                    MenuProps={{
                        style: {
                            maxHeight: 250
                        }
                    }}
                    startAdornment={props.startAdornment}
                >
                    <MenuItem value={SELECT_EMPTY}>Seçiniz</MenuItem>
                    {items.map((item: T, index: number) => {
                        const val = item[props.options.displayValue] as any;
                        return (
                            <MenuItem
                                key={index}
                                value={val}
                                className="custom-select-list-item">
                                {item[props.options.displayField] as string}
                            </MenuItem>
                        )
                    })}
                </SelectInput>
                {props.errorText && <FormHelperText>{props.errorText}</FormHelperText>}
                {props.loading && <CircularProgress thickness={5} size={20} className="progress" />}
            </FormControl>
        </ThemeProvider>
    )
}

const SelectNoGuidance = <T extends {}>(props: CustomSelectProps<T>) => {

    const items = props.options.data;

    return (
        <ThemeProvider theme={theme}>
            <FormControl fullWidth size='small' className='custom-select'>
                <InputLabel id={props.id ? props.id + '-label' : undefined} error={!IsNullOrEmpty(props.errorText)}>
                    {`${props.caption} ${props.required ? '*' : ''}`}
                </InputLabel>
                <SelectInput
                    id={props.id}
                    name={props.name}
                    className={props.className}
                    value={props.options.selected === null ? SELECT_EMPTY : props.options.selected}
                    size="small"
                    onChange={props.onChange}
                    disabled={props.disabled}
                    readOnly={props.readOnly}
                    input={(
                        <OutlinedInput
                            label={props.caption}
                            required={props.required} />
                    )}
                    error={!IsNullOrEmpty(props.errorText)}
                    MenuProps={{
                        style: {
                            maxHeight: 250
                        }
                    }}
                    startAdornment={props.startAdornment}
                >
                    {items.map((item: T, index: number) => {
                        const val = item[props.options.displayValue] as any;
                        return (
                            <MenuItem
                                key={index}
                                value={val}
                                className="custom-select-list-item">
                                {item[props.options.displayField] as string}
                            </MenuItem>
                        )
                    })}
                </SelectInput>
                {props.errorText && <FormHelperText>{props.errorText}</FormHelperText>}
                {props.loading && <CircularProgress thickness={5} size={20} className="progress" />}
            </FormControl>
        </ThemeProvider>
    )
}

export default Select;
export { SelectNoGuidance };